import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import Tabs from "@/components/Tabs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFSelectNew from "@/components/Input/SelectNew";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import EmptyData from "@/components/EmptyData";
import VLFModal from "@/components/Modal";

import {RosterAPI} from "@/api/roster";
import {MatchAPI} from "@/api/match";

import {setTeamTab} from "@/actions";

import ArrowBack from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	playerRoles,
} from "@/utils/constants";

import "./index.scss";
import {useTranslation} from "react-i18next";

const playersStatusOptions = [
	{
		label: "Tutti",
		value: "all",
	},
	{
		label: "Rosa",
		value: ["Interno", "Prestito_in"],
	},
];

const ManagerFootballTeamPage = (props) => {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const currentTab = useSelector((state) => state.teamTab);
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [team, setTeam] = useState();
	const [playersStatus, setPlayersStatus] = useState(playersStatusOptions[0]);
	const [lastLeagueMatches, setLastLeagueMatches] = useState([]);

	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);

	const getTeam = async () => {
		setLoading(true);
		try {
			const response = await RosterAPI.getById(id);
			setTeam(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteTeam = async () => {
		setLoading(true);
		try {
			await RosterAPI.delete(id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/manager/teams");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	useEffect(() => {
		getTeam();
	}, [id]);

	useEffect(() => {
		if (team?.playerCategory?.id) {
			getLastLeagueMatches();
		}
	}, [team, seasonId]);

	const goBack = () => {
		navigate("/manager/teams");
	};

	const getLastLeagueMatches = async () => {
		try {
			const response = await MatchAPI.list({
				categoryId: team?.playerCategory?.id,
				seasonId: team?.season?.id || seasonId,
			});
			const now = moment();
			const filteredMatches = (response || [])
				.filter(
					(match) =>
						match?.matchType === "Campionato" &&
						match?.startDate &&
						moment(match.startDate).isSameOrBefore(now),
				)
				.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
				.slice(0, 5);
			setLastLeagueMatches(filteredMatches);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const mainConfiguration = [
		{
			value: team?.season?.name,
			label: "Stagione",
			size: "col-6 col-lg-4",
		},
		{
			value: team ? team?.team?.name : "---",
			label: "Squadra",
			size: "col-6 col-lg-4",
		},
		{
			value: team?.playerCategory?.name,
			label: "Categoria",
			size: "col-6 col-lg-4",
		},
	];

	const groupedPlayers = {};
	playerRoles.forEach((pr) => {
		const roleLabel = pr.groupLabel;
		const roleId = pr.value;
		const playersMembers = team?.players
			?.filter(
				(p) =>
					(p.player?.goalkeeper && roleId === "Portiere") ||
					p.player?.primaryRole === roleId,
			)
			?.filter(
				(p) =>
					playersStatus?.value === "all" ||
					playersStatus?.value?.indexOf(p.player?.status) !== -1,
			);

		if (playersMembers?.length) {
			groupedPlayers[roleLabel] = playersMembers;
		}
	});

	const playersListConfiguration = {
		data: groupedPlayers,
		onClick: (player) =>
			!player?.player?.prospect
				? navigate("/players/" + player?.player?.id)
				: navigate("/scouting/players/" + player?.player?.id),
		customClassName: (player) => {
			return player?.player?.status === "Prestito" ? "loan" : "";
		},
		separator: false,
	};

	const staffListConfiguration = {
		data: team?.staff,
		onClick: (staff) =>
			!staff?.staff.prospect
				? navigate("/manager/staffs/" + staff?.staff.id)
				: navigate("/scouting/staffs/" + staff?.staff.id),
	};

	const lastMatchesColumns = [
		{
			label: "Data",
			key: "startDate",
			customRender: (item) =>
				item?.startDate ? moment(item.startDate).format("DD/MM/YYYY") : "---",
			small: true,
		},
		{
			label: "Partita",
			key: "match",
			align: "start",
			customRender: (item) =>
				item?.homeClub && item?.awayClub
					? `${item.homeClub} - ${item.awayClub}`
					: "---",
		},
		{
			label: "Risultato",
			key: "result",
			small: true,
			align: "center",
			customRender: (item) => {
				if (
					item?.homeClubGoals === null ||
					item?.homeClubGoals === undefined ||
					item?.awayClubGoals === null ||
					item?.awayClubGoals === undefined
				) {
					return "---";
				}
				return `${item.homeClubGoals} - ${item.awayClubGoals}`;
			},
		},
		{
			label: "Giornata",
			key: "round",
			small: true,
			align: "end",
			customRender: (item) =>
				item?.round !== null && item?.round !== undefined ? item.round : "---",
		},
	];

	let mainActions = [];
	switch (currentTab) {
		case "main_info":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
				{
					icon: <EditIcon />,
					title: "Modifica",
					onClick: () => navigate("/manager/teams/" + team?.id + "/edit"),
				},
				{
					icon: <DeleteIcon />,
					title: "Elimina",
					onClick: () => setOpenHandleConfirm(true),
					color: "error",
				},
			];

			break;
		case "players":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
				{
					icon: <EditIcon />,
					title: "Modifica",
					onClick: () => navigate("/manager/teams/" + id + "/players"),
				},
			];
			break;
		case "staffs":
			mainActions = [
				{
					icon: <ArrowBack />,
					title: "Indietro",
					onClick: goBack,
					color: "info",
				},
				{
					icon: <EditIcon />,
					title: "Modifica",
					onClick: () => navigate("/manager/teams/" + id + "/staffs"),
				},
			];
			break;
		default:
			break;
	}

	const breadcrumbsElements = [
		{label: "Rose", path: "/manager/teams"},
		{label: "/"},
	];

	const tabs = [
		{
			key: "main_info",
			label: t("informations"),
			render: () => {
				return (
					<div className="row no-margin g-2">
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								content={
									<div className="row">
										<div className="col-sm-12">
											<ViewField configuration={mainConfiguration} />
										</div>
									</div>
								}
								openable={false}
							/>
						</div>
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Ultime gare"
								openable={false}
								content={
									lastLeagueMatches?.length ? (
										<CardsTable
											configurationColumns={lastMatchesColumns}
											data={lastLeagueMatches}
											fullHeight={false}
											middleHeight
											handleClick={(match) => {
												if (match?.id) {
													navigate("/matches/" + match.id);
												}
											}}
										/>
									) : (
										<EmptyData />
									)
								}
							/>
						</div>
					</div>
				);
			},
		},
		{
			key: "players",
			label: t("players"),
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small">
							<div className="d-flex align-items-center justify-content-end">
								<div className="filter-container">
									<VLFSelectNew
										options={playersStatusOptions}
										sorted={false}
										isClearable={false}
										value={playersStatus}
										onChange={(newValue) => {
											setPlayersStatus(newValue);
										}}
									/>
								</div>
							</div>
						</div>
						<div className="col-sm-12 padding-small">
							<CardsList
								type="roster_player"
								grouped
								breadcrumbs
								tabbed
								customFilters
								listConfiguration={playersListConfiguration}
								defaultSortedField="player.lastName"
								groupSorted={false}
							/>
						</div>
					</div>
				);
			},
		},
		{
			key: "staffs",
			label: t("staff"),
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small">
							<CardsList
								type="roster_staff"
								breadcrumbs
								tabbed
								listConfiguration={staffListConfiguration}
								defaultSortedField="staff.lastName"
							/>
						</div>
					</div>
				);
			},
		},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={team ? team.playerCategory?.name : ""}
				actions={<MainActions actions={mainActions} />}
			/>
			<Tabs
				tabs={tabs}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setTeamTab(tab))}
			/>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					deleteTeam();
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
};

export default ManagerFootballTeamPage;
